// src/lib/capExampleEvidence.ts
// Resolves control-specific "Example Evidence" lists for CAP items.
// Falls back to a generic list only when no control-specific evidence exists.

import { CONTROL_DETAILS } from "@/lib/hoverDetails";
import { INFRASTRUCTURE_CONTROL_GUIDANCE } from "@/configs/controlGuidance/infrastructureSecurityGuidance";
import { REVIEWER_CONTROL_GUIDANCE } from "@/configs/controlGuidance/reviewerControlGuidance";
import { evidenceExamplesForServiceModels } from "@/configs/telecom/serviceModelEvidenceExamples";

export const DEFAULT_EXAMPLE_EVIDENCE: string[] = [
  "Policies or standard operating procedures",
  "Screenshots of configured controls or dashboards",
  "System logs or audit trails",
  "Reports, test results, or attestations",
];

function controlCodeCandidates(code: string): string[] {
  const raw = code.trim().toUpperCase();
  const out = new Set<string>([raw]);
  // Strip common prefixes
  const stripped = raw.replace(/^NIST-CSF-/, "").replace(/^NIST-/, "");
  out.add(stripped);
  // Dotted form: GV-RM-01 -> GV.RM-01
  const dotted = stripped
    .replace(/^([A-Z]{2})-([A-Z]{2})-(\d{1,3})$/, "$1.$2-$3")
    .replace(/^([A-Z]{2})-([A-Z]{2})$/, "$1.$2");
  out.add(dotted);
  // Drop trailing index: GV.RM-01 -> GV.RM
  const noIndex = dotted.replace(/-\d{1,3}$/, "");
  out.add(noIndex);
  // Try zero-padded vs unpadded numeric variants: ID.RA-1 <-> ID.RA-01
  const m = dotted.match(/^([A-Z]{2}\.[A-Z]{2})-(\d{1,3})$/);
  if (m) {
    const num = parseInt(m[2], 10);
    out.add(`${m[1]}-${num}`);
    out.add(`${m[1]}-${String(num).padStart(2, "0")}`);
  }
  return [...out].filter(Boolean);
}

/**
 * Resolve control-specific example evidence for a CAP item.
 * Priority:
 *  1. Infrastructure Security mapping (when sourceKey is provided)
 *  2. CONTROL_DETAILS hover registry (NIST CSF questionnaire controls)
 *  3. REVIEWER_CONTROL_GUIDANCE (evidence_to_review list)
 *  4. DEFAULT_EXAMPLE_EVIDENCE (generic fallback)
 */
export interface ResolveExampleEvidenceResult {
  items: string[];
  source: "infrastructure" | "control" | "reviewer" | "fallback";
  /**
   * Phase 2.9.1 — Per-service-model additive evidence buckets. Always
   * presented after the core items; never mutates the core list.
   */
  serviceModelGroups: { model: string; items: string[] }[];
}

export function resolveExampleEvidence(opts: {
  controlCode?: string | null;
  sourceType?: string | null;
  sourceKey?: string | null;
  /** Phase 2.9.1 — Service models inherited from the Portfolio Company. */
  serviceModels?: string[] | null;
}): ResolveExampleEvidenceResult {
  const { controlCode, sourceType, sourceKey, serviceModels } = opts;
  const serviceModelGroups = evidenceExamplesForServiceModels(serviceModels ?? []);

  // 1. Infrastructure Security
  if (sourceType === "infrastructure_security" && sourceKey) {
    const infra = INFRASTRUCTURE_CONTROL_GUIDANCE[sourceKey];
    if (infra?.evidence?.length) {
      return { items: infra.evidence, source: "infrastructure", serviceModelGroups };
    }
  }

  // 2. CONTROL_DETAILS lookup
  if (controlCode) {
    for (const cand of controlCodeCandidates(controlCode)) {
      const detail = CONTROL_DETAILS[cand];
      if (detail?.evidence?.length) {
        return { items: detail.evidence, source: "control", serviceModelGroups };
      }
    }

    // 3. Reviewer guidance fallback
    for (const cand of controlCodeCandidates(controlCode)) {
      const rg = REVIEWER_CONTROL_GUIDANCE[cand];
      if (rg?.evidence_to_review?.length) {
        return { items: rg.evidence_to_review, source: "reviewer", serviceModelGroups };
      }
    }
  }

  return { items: DEFAULT_EXAMPLE_EVIDENCE, source: "fallback", serviceModelGroups };
}

/**
 * Normalize an evidence URL so that browser anchors don't treat it as a relative
 * path (which routes through the SPA and lands on the login page). Returns null
 * for empty values. Leaves valid http(s) and storage paths alone.
 */
export function normalizeEvidenceUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  // Already absolute http(s)
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Other absolute schemes (mailto:, ftp:, etc.) - leave as-is
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  // Looks like a domain (contains a dot, no leading slash) -> prepend https://
  if (/^[^/\s]+\.[^/\s]/.test(trimmed)) return `https://${trimmed}`;
  // Otherwise return as-is (could be a storage path handled elsewhere)
  return trimmed;
}
